import { Box, Stack } from '@mantine/core';
import type { Message, SecodaAIReferencedObjectOut } from '@repo/api-codegen';
import { CopyButton } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { MessageContentV3 } from '../../../components/AIAssistant/Messages/MessageContentV3';
import { TokenUsageButton } from '../../../components/AIAssistant/Messages/TokenUsageButton';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { AiReferencedObjectCard } from '../../AiReferencedObjectCard';
import type { AiChatStore } from '../store';
import type { PrefilledAgentData } from '../types';
import { AskQuestionButton } from './AskQuestionButton';
import { CreateAgentMenuItem } from './CreateAgentMenuItem';
import { MessageAiError } from './MessageAiError';
import { MessageAiLoading } from './MessageAiLoading';
import { MessageToolbar } from './MessageToolbar';
import { NegativeFeedbackButton } from './NegativeFeedbackButton';
import { OverflowMenu } from './OverflowMenu';
import { PositiveFeedbackButton } from './PositiveFeedbackButton';

export interface AiMessageProps {
	id: string;
	store: AiChatStore;
	onReferencedObjectClick?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => void;
	shouldShowInlineReferencedObject?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => boolean;
	onCreateAgent?: (data: PrefilledAgentData) => void;
}

export const AgentMessage = observer(
	({
		id,
		store,
		onReferencedObjectClick,
		shouldShowInlineReferencedObject,
		onCreateAgent,
	}: AiMessageProps) => {
		const message = store.getMessage(id)!;

		const messageContent = message.prompt.response?.content ?? '';

		const { biDashboardsEnabled } = useFeatureFlags();

		const messages = useMemo(
			() =>
				message.prompt.structured_response?.length
					? message.prompt.structured_response
					: [
							{
								content: messageContent,
								sender: 'assistant',
								use_tools: [],
								tool_response: null,
							} as Message,
						],
			[message.prompt.structured_response, messageContent]
		);

		const handleOnRetry = useCallback(() => {
			store.retry(id);
		}, [store, id]);

		const shouldDisplayLoading =
			message.prompt.status === 'pending' ||
			(message.prompt.status === 'running' &&
				messageContent.length === 0 &&
				!message.prompt.structured_response?.length);
		const shouldDisplayFailure = message.prompt.status === 'failed';
		const shouldDisplayAnswerToolbar = message.prompt.status === 'completed';
		const isLastMessage = store.isLastMessage(id);

		const handleCreateAgentFromMenu = useCallback(() => {
			onCreateAgent?.({
				title: '',
				prompt: message.prompt.prompt || '',
				cronExpression: '',
			});
		}, [message.prompt.prompt, onCreateAgent]);

		const extraMenuItems = useMemo(() => {
			const items = [];

			items.push(
				<CreateAgentMenuItem
					key="create-agent"
					onClick={handleCreateAgentFromMenu}
				/>
			);

			const hasTokenData =
				message.prompt.input_tokens ||
				message.prompt.output_tokens ||
				message.prompt.total_tokens;

			if (hasTokenData) {
				items.push(
					<TokenUsageButton
						key="token-usage"
						modelName={message.prompt.model_name}
						inputTokens={message.prompt.input_tokens}
						outputTokens={message.prompt.output_tokens}
						totalTokens={message.prompt.total_tokens}
						cacheCreationInputTokens={
							message.prompt.cache_creation_input_tokens
						}
						cacheReadInputTokens={message.prompt.cache_read_input_tokens}
					/>
				);
			}

			return items;
		}, [
			message.prompt.input_tokens,
			message.prompt.output_tokens,
			message.prompt.total_tokens,
			message.prompt.model_name,
			message.prompt.cache_creation_input_tokens,
			message.prompt.cache_read_input_tokens,
			handleCreateAgentFromMenu,
		]);

		const limitExceeded = useMemo(() => {
			if (!shouldDisplayFailure) return false;
			const failReasonLower =
				message.prompt.benchmark_raw?.fail_reason?.toLowerCase();
			return (
				failReasonLower?.includes('too long') ||
				failReasonLower?.includes('token limit') ||
				false
			);
		}, [shouldDisplayFailure, message.prompt.benchmark_raw?.fail_reason]);
		return (
			<MessageToolbar.Wrapper>
				{shouldDisplayLoading && (
					<MessageAiLoading steps={message.prompt.response?.steps ?? []} />
				)}
				{shouldDisplayFailure && (
					<MessageAiError
						onRetry={handleOnRetry}
						isLastMessage={isLastMessage}
						limitExceeded={limitExceeded}
					/>
				)}
				{!shouldDisplayLoading && !shouldDisplayFailure && (
					<Box>
						{['running', 'completed', 'stopped'].includes(
							message.prompt.status ?? ''
						) && (
							<MessageContentV3
								messages={messages}
								isRunning={message.prompt.status === 'running'}
							/>
						)}
						{biDashboardsEnabled && (
							<Stack mt="md">
								{message.prompt.referenced_objects.map(
									(referencedObject, index) => (
										<AiReferencedObjectCard
											onReferencedObjectClick={onReferencedObjectClick}
											referencedObject={referencedObject}
											key={referencedObject.id}
											promptId={message.prompt.id}
											messageId={id}
											shouldAutoOpenReferencedObjects={store.shouldAutoOpenReferencedObjectsForMessage(
												id
											)}
											isLastMessage={store.isLastMessage(id)}
											shouldShowInlineReferencedObject={
												shouldShowInlineReferencedObject
											}
											index={index}
										/>
									)
								)}
							</Stack>
						)}
						{shouldDisplayAnswerToolbar && (
							<MessageToolbar>
								<CopyButton value={message.prompt.response?.content || ''} />
								<PositiveFeedbackButton id={id} store={store} />
								<NegativeFeedbackButton id={id} store={store} />
								{!!store.initialPrompt && (
									<AskQuestionButton
										onClick={() => {
											store.onCreateQuestion(
												message.prompt,
												store.initialPrompt!,
												'response'
											);
										}}
									/>
								)}
								{extraMenuItems.length > 0 && (
									<OverflowMenu menuItems={extraMenuItems} />
								)}
							</MessageToolbar>
						)}
					</Box>
				)}
			</MessageToolbar.Wrapper>
		);
	}
);
